// ~/horizon/assets/personalise-canvas.js
//
// Boot script for the embedded personalise-PDP canvas. Pure vanilla JS,
// no framework. Responsibilities:
//
//   1. Load the personalise-canvas bundle (window.PersonaliseCanvas).
//   2. Mount the canvas into [data-personalise-canvas-host].
//   3. Wire DOM events from sku-picker rows ([data-personalise-row]) to
//      window.PersonaliseCanvas.setState().
//   4. Manage the upload → S3 → AI-restyle state machine
//      (see assets/personalise-state.js).
//   5. Mirror the design to hidden cart-form properties so the standard
//      Shopify ATC carries _design_id + _rendered_image_url.
//   6. Debounce-autosave design state to /a/advisor/api/designs/autosave.
//   7. Rasterise + POST /a/advisor/api/designs/render after each settled
//      design (Done states).
//   8. Catch ThemeEvents.cartUpdate → PATCH /a/advisor/api/designs/attach-cart
//      with the cart line key.

import { reduce, INITIAL } from './personalise-state.js';
import { resilientFetch } from './personalise-net.js';
import {
  writeSnapshot, readSnapshot, clearSnapshot, decideResume,
} from './personalise-resume.js';

(() => {
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  function readAppUrl() {
    return document.querySelector('[data-personalise-app-url]')?.dataset.personaliseAppUrl
      || '/a/advisor';
  }
  function readTemplateHandle() {
    return document.querySelector('[data-personalise-template-handle]')?.dataset.personaliseTemplateHandle
      || null;
  }
  function readProductHandle() {
    return document.querySelector('[data-personalise-canvas-host]')?.dataset.productHandle
      || null;
  }

  // ── Bundle loader ── lazy-load the personalise-canvas bundle if it isn't
  // already on the page.
  async function ensureBundle() {
    if (window.PersonaliseCanvas) return;
    const base = readAppUrl();
    const cssLink = document.createElement('link');
    cssLink.rel = 'stylesheet';
    cssLink.href = `${base}/personalise-canvas-bundle.css`;
    document.head.appendChild(cssLink);
    await new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = `${base}/personalise-canvas-bundle.js`;
      s.onload = resolve;
      s.onerror = reject;
      document.body.appendChild(s);
    });
  }

  // ── Template fetch ── Server resolves the template + bakes svgUrl. First
  // check if there's a saved design — its `template` is the canonical shape
  // we want. If not, hit the style route with probe=1 to get just the template.
  async function fetchTemplate(templateHandle, productHandle) {
    const base = readAppUrl();
    try {
      const mineRes = await fetch(`${base}/api/designs/mine?productHandle=${encodeURIComponent(productHandle)}`, { credentials: 'include' });
      if (mineRes.ok) {
        const mine = (await mineRes.json()) || {};
        if (mine.template?.id) return { template: mine.template, design: mine.design || null };
      }
    } catch {/* fall through */}
    // Fall back: probe the style route for the template (no styler run).
    // The style route's GET handler / probe param should return the template
    // shape. If unimplemented, the fetch fails → throw and we surface BLOCKED.
    const tplRes = await fetch(`${base}/api/personalise/style?probe=1&templateHandle=${encodeURIComponent(templateHandle)}`);
    if (!tplRes.ok) throw new Error('template fetch failed');
    return { template: (await tplRes.json()).template, design: null };
  }

  // ── State machine ──
  let state = INITIAL;

  // Resume bookkeeping (Phase-1 mobile resilience).
  let activeOperation = null;        // 'upload' | 'restyle' | null — a fetch is genuinely in-flight in THIS context
  let lastFile = null;               // the File we last tried to upload, for redrive-upload
  let isResuming = false;            // drives the "Reconnecting…" sub-status
  let resumeAttempts = 0;            // capped auto-resume budget per session
  const MAX_RESUME_ATTEMPTS = 3;
  const safeSession = () => {
    try { return window.sessionStorage; } catch (_) { return null; }
  };

  // ── Paint-phase narration ──
  // The /style call is one long fetch with no progress events, so the paint
  // segment eases against the expected duration (snap to 100 on completion)
  // and the copy rotates — a static spinner reads as "hung", rotating
  // concrete copy reads as "working".
  const PAINT_EXPECTED_S = 45; // ~p50 of /api/personalise/style; calibrate from telemetry
  const PAINT_COPY = [
    'Mapping your photo…',
    'Holding the likeness…',
    'Painting brushwork…',
    'Blending the palette…',
    'Final touches…',
  ];
  let paintTimer = null;
  let paintT0 = 0;
  let lastCopyIdx = -1;
  let pendingReveal = false; // set on RESTYLE_DONE; consumed when the styled snapshot lands in the gallery

  function dispatch(action) {
    state = reduce(state, action);
    render();
    // Persist a replay ticket on every transition. A fresh genuine upload
    // resets the auto-resume budget; an empty/reset state clears the ticket.
    if (action.type === 'UPLOAD_START') resumeAttempts = 0;
    if (state.phase === 'empty') {
      clearSnapshot(safeSession());
    } else {
      writeSnapshot(safeSession(), state);
    }
  }

  // ── Rendering: keeps DOM in sync with state ──
  function render() {
    if (state.styledUrl) {
      window.PersonaliseCanvas.setState({ styledPhotoUrl: state.styledUrl, styledPhotoS3Key: state.styledKey });
    } else if (state.photoUrl) {
      // Optimistic: show the raw photo while restyle runs
      window.PersonaliseCanvas.setState({ styledPhotoUrl: state.photoUrl });
    }
    const photoRow = document.querySelector('[data-personalise-row="photo"]');
    if (photoRow) {
      photoRow.dataset.phase = state.phase;
      // Filename appears in both uploading and done states.
      const fileName = state.fileName || 'Photo';
      const setText = (sel, val) => {
        const el = photoRow.querySelector(sel);
        if (el) el.textContent = val;
      };
      setText('[data-photo-filename]', fileName);
      setText('[data-photo-filename-done]', fileName);
      // Upload sub-status. (No percent — the upload is a single fetch with
      // no progress events, so a number sat frozen at 0; the journey bar's
      // indeterminate sweep carries the motion. Restyle narration — timer,
      // rotating copy, "Reconnecting…" — lives in paintTick.)
      const isHeic = /\.(heic|heif)$/i.test(fileName);
      setText('[data-photo-substatus]', isHeic ? 'Converting HEIC → uploading' : 'Uploading to secure storage');
      // Done-state thumbnail: use the styled URL if we have one, else the
      // raw photo as a placeholder.
      const thumbUrl = state.styledUrl || state.photoUrl;
      const thumb = photoRow.querySelector('[data-photo-thumb]');
      if (thumb && thumbUrl) {
        thumb.style.backgroundImage = `url("${thumbUrl}")`;
      }
      // Error message (only override the static copy when we have one)
      if (state.lastError) setText('[data-photo-error]', state.lastError);
    }
    // ATC enabled only in 'done'
    document.querySelectorAll('form[action*="/cart/add"] button[type="submit"], form[action*="/cart/add"] button[name="add"], #sticky-atc-btn').forEach(btn => {
      btn.disabled = !state.canAtc;
    });
    if (state.phase === 'done') {
      ensureCartLineProps({ photoS3Key: state.photoS3Key, styledKey: state.styledKey });
    }
    syncProgressUi();
  }

  // ── Journey bar + wet-paint overlay + artwork chip ──
  // The status box carries the detail; the artwork itself visibly stays
  // "wet" until the styled snapshot lands, so the optimistic raw-photo
  // render can't be mistaken for the finished piece.

  function mockScope() {
    const host = document.querySelector('[data-personalise-canvas-host]');
    return host?.closest('.shopify-section')
      || host?.closest('product-page, product-information, .product-information')
      || null;
  }

  function setWet(on) {
    const scope = mockScope();
    if (!scope) return;
    scope.classList.toggle('personalise-wet', !!on);
    if (on) scope.classList.remove('personalise-reveal');
  }

  function playReveal() {
    const scope = mockScope();
    if (!scope) return;
    scope.classList.remove('personalise-wet');
    scope.classList.add('personalise-reveal');
    setTimeout(() => scope.classList.remove('personalise-reveal'), 1400);
    const chip = ensureChip();
    if (chip) {
      chip.hidden = false;
      chip.className = 'personalise-artchip personalise-artchip--ok';
      chip.querySelector('.personalise-artchip__label').textContent = 'Style applied';
      setTimeout(() => chip.classList.add('personalise-artchip--fading'), 2200);
      setTimeout(() => { chip.hidden = true; chip.classList.remove('personalise-artchip--fading'); }, 2700);
    }
  }

  function ensureChip() {
    const scope = mockScope();
    if (!scope) return null;
    let chip = scope.querySelector('.personalise-artchip');
    if (chip) return chip;
    // Pin to the hero frame-mock container; fall back to the first mock.
    // The gallery may render after boot, so this re-queries lazily.
    const hostEl = scope.querySelector('[data-slide-role="frame-mock"] .mock-image-container')
      || scope.querySelector('.mock-image-container');
    if (!hostEl) return null;
    if (getComputedStyle(hostEl).position === 'static') hostEl.style.position = 'relative';
    chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'personalise-artchip';
    chip.hidden = true;
    chip.innerHTML = '<span class="personalise-artchip__ring" aria-hidden="true"></span><span class="personalise-artchip__label"></span>';
    chip.addEventListener('click', () => {
      if (chip.classList.contains('personalise-artchip--bad') && state.phase === 'failed' && state.photoUrl) {
        runRestyle(state.photoUrl);
      }
    });
    hostEl.appendChild(chip);
    return chip;
  }

  function startPaint() {
    if (paintTimer) return; // already narrating (re-render mid-restyle)
    paintT0 = Date.now();
    lastCopyIdx = -1;
    paintTick();
    paintTimer = setInterval(paintTick, 500);
  }

  function stopPaint() {
    if (paintTimer) clearInterval(paintTimer);
    paintTimer = null;
  }

  function paintTick() {
    const photoRow = document.querySelector('[data-personalise-row="photo"]');
    if (!photoRow) return;
    const t = (Date.now() - paintT0) / 1000;
    // Eases toward ~92% at the expected duration, keeps crawling beyond it
    // (never reaches 100 — completion snaps it). Honest about slow runs.
    const p = Math.min(90, 100 * (1 - Math.exp(-t / (PAINT_EXPECTED_S / 2.5))));

    const seg = photoRow.querySelector('[data-journey-seg="paint"] i');
    if (seg) seg.style.width = p.toFixed(1) + '%';

    const timer = photoRow.querySelector('[data-restyle-timer]');
    if (timer) timer.textContent = Math.floor(t) + 's · usually ~' + PAINT_EXPECTED_S + 's';

    const sub = photoRow.querySelector('[data-restyle-sub]');
    if (sub) {
      const idx = Math.floor(t / 6) % PAINT_COPY.length;
      if (isResuming) {
        sub.textContent = 'Reconnecting… picking up where we left off';
        lastCopyIdx = -1;
      } else if (idx !== lastCopyIdx) {
        lastCopyIdx = idx;
        sub.classList.remove('is-rotating');
        void sub.offsetWidth; // restart the entrance animation
        sub.classList.add('is-rotating');
        sub.innerHTML = '';
        sub.appendChild(document.createTextNode(PAINT_COPY[idx] + ' '));
        const safe = document.createElement('span');
        safe.className = 'psr__safe';
        safe.textContent = 'Safe to lock your phone.';
        sub.appendChild(safe);
      }
    }

    const chip = ensureChip();
    if (chip) {
      chip.hidden = false;
      chip.className = 'personalise-artchip';
      chip.style.setProperty('--p', p.toFixed(0));
      chip.querySelector('.personalise-artchip__label').textContent =
        'Painting your style · ' + Math.floor(t) + 's';
    }
  }

  function syncProgressUi() {
    const photoRow = document.querySelector('[data-personalise-row="photo"]');
    if (!photoRow) return;
    const segUp = photoRow.querySelector('[data-journey-seg="upload"] i');
    const segPaint = photoRow.querySelector('[data-journey-seg="paint"] i');
    if (!segUp || !segPaint) return;
    const chip = mockScope()?.querySelector('.personalise-artchip') || null;

    switch (state.phase) {
      case 'uploading':
        // Clear the inline width so the CSS indeterminate sweep applies.
        segUp.style.width = '';
        segPaint.style.width = '0%';
        stopPaint();
        pendingReveal = false;
        setWet(false);
        if (chip) chip.hidden = true;
        break;
      case 'restyling':
        segUp.style.width = '100%';
        setWet(true);
        startPaint();
        break;
      case 'done':
        segUp.style.width = '100%';
        segPaint.style.width = '100%';
        stopPaint();
        // pendingReveal: the wet treatment stays until the styled snapshot
        // actually lands in the gallery (applySnapshotToGallery → playReveal).
        // A rehydrated/saved design never set it — clear everything now.
        if (!pendingReveal) {
          setWet(false);
          if (chip) chip.hidden = true;
        }
        break;
      case 'failed': {
        segUp.style.width = '100%';
        stopPaint();
        setWet(false);
        const failChip = ensureChip();
        if (failChip) {
          failChip.hidden = false;
          failChip.className = 'personalise-artchip personalise-artchip--bad';
          failChip.querySelector('.personalise-artchip__label').textContent = 'Couldn’t finish — tap to retry';
        }
        break;
      }
      default: // empty
        segUp.style.width = '0%';
        segPaint.style.width = '0%';
        stopPaint();
        pendingReveal = false;
        setWet(false);
        if (chip) chip.hidden = true;
        break;
    }
  }

  // ── Cart line properties ──
  function ensureCartLineProps({ photoS3Key, styledKey }) {
    const form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    const set = (name, value) => {
      let input = form.querySelector(`input[name="properties[${name}]"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${name}]`;
        form.appendChild(input);
      }
      input.value = value || '';
    };
    if (photoS3Key) set('_photo_s3_key', photoS3Key);
    if (styledKey) set('_styled_photo_s3_key', styledKey);
    // _rendered_image_url is populated by syncCartProps() (styled photo);
    // _preview_image_url (full composite raster) by persistRender().
  }

  // ── Heic2any lazy-loader (mirrors assets/photo-upload.js) ──
  let _heic2anyPromise = null;
  function loadHeic2AnyOnce() {
    if (typeof window.heic2any === 'function') return Promise.resolve();
    if (_heic2anyPromise) return _heic2anyPromise;
    _heic2anyPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-heic2any]');
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error('heic2any failed to load')));
        return;
      }
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js';
      s.async = true;
      s.dataset.heic2any = 'true';
      s.onload = () => resolve();
      s.onerror = () => { _heic2anyPromise = null; reject(new Error('heic2any failed to load')); };
      document.head.appendChild(s);
    });
    return _heic2anyPromise;
  }

  async function convertHeicToJpeg(file) {
    await loadHeic2AnyOnce();
    const convertedBlob = await window.heic2any({ blob: file, toType: 'image/jpeg', quality: 0.95 });
    const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
    const fileName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
    return new File([blob], fileName, { type: 'image/jpeg', lastModified: Date.now() });
  }

  // ── Photo upload pipeline ──
  async function handlePhotoSelected(file) {
    dispatch({ type: 'UPLOAD_START', fileName: file.name });
    // Claim the in-flight slot synchronously, before the first await, so a
    // double attemptResume() (boot + pageshow both fire on load) can't start a
    // second upload while this one is parked on HEIC conversion / file read.
    activeOperation = 'upload';
    try {
      // Convert HEIC/HEIF to JPEG before upload — Gemini + our raster pipeline
      // don't accept HEIC. Lazy-loaded so non-HEIC uploads pay zero cost.
      const isHeicByExt = /\.(heic|heif)$/i.test(file.name);
      const effectiveType = file.type || (isHeicByExt ? 'image/heic' : '');
      if (effectiveType === 'image/heic' || effectiveType === 'image/heif') {
        try {
          file = await convertHeicToJpeg(file);
        } catch (err) {
          activeOperation = null;
          console.error('[personalise] HEIC conversion failed', err);
          dispatch({ type: 'UPLOAD_FAIL', error: 'HEIC photos couldn\'t be converted. On iPhone, set Camera → Formats → Most Compatible and re-shoot.' });
          return;
        }
      }

      lastFile = file;
      const dataUrl = await fileToDataUrl(file);
      const base = readAppUrl();
      const upRes = await resilientFetch(`${base}/api/upload-photo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dataUrl, filename: file.name }),
      }, { timeoutMs: 30000 });
      const upJson = await upRes.json();
      activeOperation = null;
      dispatch({ type: 'UPLOAD_DONE', photoS3Key: upJson.key, photoUrl: upJson.url });
      await runRestyle(upJson.url);
    } catch (err) {
      activeOperation = null;
      console.error('[personalise] upload failed', err);
      dispatch({ type: 'UPLOAD_FAIL', error: err.message, retryable: !!err.retryable });
    }
  }

  async function runRestyle(photoUrl) {
    dispatch({ type: 'RESTYLE_START' });
    activeOperation = 'restyle';
    try {
      const styled = await pollRestyle(photoUrl);
      activeOperation = null;
      isResuming = false;
      resumeAttempts = 0; // a clean success resets the auto-resume budget
      // Keep the wet-paint treatment on the gallery until the STYLED
      // snapshot actually lands there (applySnapshotToGallery plays the
      // reveal) — dropping it now would briefly present the raw photo as
      // finished while the new snapshot rasterises.
      pendingReveal = true;
      dispatch({ type: 'RESTYLE_DONE', styledUrl: styled.styledUrl, styledKey: styled.s3Key });
      // Set the cart line properties immediately from the style response.
      // The styled photo URL IS the rendered asset for v1 — it's the image
      // the customer will receive.
      syncCartProps({
        renderedUrl: styled.styledUrl,
        styledKey: styled.s3Key,
      });
      // Persist the design now the styled photo exists. Autosave otherwise
      // only fires on text/palette edits, so a photo-only personalisation
      // would never create a Design row — and the post-order print pipeline
      // resolves the configuration through it (via _design_id).
      scheduleAutosave();
    } catch (err) {
      activeOperation = null;
      isResuming = false;
      console.error('[personalise] restyle failed', err);
      dispatch({ type: 'RESTYLE_FAIL', error: err.message, retryable: !!err.retryable });
    }
  }

  // The styler routinely outlasts the Shopify app-proxy timeout, so /style is a
  // poll endpoint: each POST returns { status: 'done' | 'pending' | 'failed' }
  // within the proxy budget, and we re-POST the same body until it settles. The
  // server shares one in-flight run per (photo, template), so polling never
  // starts a duplicate generation. A network blip gets one quick retry; a
  // 'failed' status is terminal (we never retry the expensive generation).
  // Tolerant of deploy order: a legacy response carrying styledUrl with no
  // status is treated as done.
  async function pollRestyle(photoUrl) {
    const base = readAppUrl();
    const body = JSON.stringify({ photoUrl, templateHandle: readTemplateHandle() });
    const deadline = Date.now() + 180000; // overall safety cap (~3 min)
    let delay = 1500;
    for (;;) {
      const res = await resilientFetch(`${base}/api/personalise/style`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      }, { timeoutMs: 15000, retryDelays: [800] });
      const json = await res.json();
      if (json.status === 'done' || json.styledUrl) {
        return { styledUrl: json.styledUrl, s3Key: json.s3Key };
      }
      if (json.status === 'failed') {
        const e = new Error(json.error || 'Styling failed');
        e.retryable = false;
        throw e;
      }
      // 'pending' — keep polling until the styler finishes or we hit the cap.
      if (Date.now() > deadline) {
        const e = new Error('Styling is taking longer than expected — tap to retry');
        e.retryable = false;
        throw e;
      }
      await new Promise((r) => setTimeout(r, delay));
      delay = Math.min(Math.round(delay * 1.5), 5000);
    }
  }

  // ── Cart line property sync ──
  // V1: mirror the styled photo to the cart form so the line item carries
  // the personalised image. Server-side Design row persistence is deferred
  // until an anonymous-session strategy is in place.
  function setCartLineProp(name, value) {
    const form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    let i = form.querySelector(`input[name="properties[${name}]"]`);
    if (!i) {
      i = document.createElement('input');
      i.type = 'hidden';
      i.name = `properties[${name}]`;
      form.appendChild(i);
    }
    i.value = value || '';
  }
  function syncCartProps({ renderedUrl, styledKey }) {
    if (renderedUrl) setCartLineProp('_rendered_image_url', renderedUrl);
    if (styledKey) setCartLineProp('_styled_photo_s3_key', styledKey);
    setCartLineProp('_design_template_id', readTemplateHandle());
  }

  // ── Rasterise the live preview composite for the cart line ──
  // The styled photo alone (_rendered_image_url) isn't what the customer
  // sees in the gallery — the full artwork template (styled photo + text
  // slots + palette, arranged per the template SVG) is. Snapshot that
  // composite, upload it to S3 via the existing photo endpoint, and point
  // a dedicated _preview_image_url at the result so the cart line item
  // thumbnail matches the on-PDP preview exactly. _rendered_image_url is
  // left untouched so order fulfilment keeps the clean styled asset.
  let _persistTimer = null;
  let _persistInFlight = false;
  let _pendingDataUrl = null;
  let _lastPersistedHash = null;
  function schedulePersistRender(dataUrl) {
    clearTimeout(_persistTimer);
    _persistTimer = setTimeout(() => persistRender(dataUrl), 600);
  }
  async function persistRender(dataUrl) {
    // An upload is already running — stash the latest composite and let the
    // in-flight call pick it up when it finishes, so the final edit a user
    // makes mid-upload is never dropped.
    if (_persistInFlight) {
      if (dataUrl) _pendingDataUrl = dataUrl;
      return;
    }
    _persistInFlight = true;
    try {
      // Fall back to taking a fresh snapshot when called without one
      // (e.g. the autosave backstop) so the upload always has a composite.
      if (!dataUrl && typeof window.PersonaliseCanvas?.snapshot === 'function') {
        dataUrl = await window.PersonaliseCanvas.snapshot({ width: 1200 });
      }
      if (!dataUrl || typeof dataUrl !== 'string' || !dataUrl.startsWith('data:image/')) return;
      // Dedupe: re-uploading an identical composite wastes an S3 round-trip.
      const hash = `${dataUrl.length}:${dataUrl.slice(-48)}`;
      if (hash === _lastPersistedHash) return;
      const base = readAppUrl();
      const res = await resilientFetch(`${base}/api/upload-photo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dataUrl, filename: `personalised-preview-${readTemplateHandle() || 'design'}.png` }),
      }, { timeoutMs: 20000 });
      const json = await res.json();
      if (!json?.url) throw new Error('preview upload returned no url');
      _lastPersistedHash = hash;
      setCartLineProp('_preview_image_url', json.url);
      if (json.key) setCartLineProp('_preview_image_s3_key', json.key);
    } catch (err) {
      console.warn('[personalise] persistRender failed', err);
    } finally {
      _persistInFlight = false;
      // Flush any composite that arrived while this upload was running.
      if (_pendingDataUrl) {
        const next = _pendingDataUrl;
        _pendingDataUrl = null;
        schedulePersistRender(next);
      }
    }
  }

  // ── Helpers ──
  function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = () => reject(r.error);
      r.readAsDataURL(file);
    });
  }

  function svgToPng(svgString, longEdgePx = 2400) {
    return new Promise((resolve, reject) => {
      const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        const aspect = img.width / img.height || 1;
        const w = aspect >= 1 ? longEdgePx : Math.round(longEdgePx * aspect);
        const h = aspect >= 1 ? Math.round(longEdgePx / aspect) : longEdgePx;
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        canvas.toBlob(b => { URL.revokeObjectURL(url); resolve(b); }, 'image/png', 0.92);
      };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('svg→png decode failed')); };
      img.src = url;
    });
  }

  // Show a soft "re-add your photo" prompt when the page was reloaded mid-upload
  // and the in-memory File was discarded — nothing actually broke, the browser
  // just freed memory, so this is a prompt, not a hard error.
  function showReaddPrompt() {
    dispatch({ type: 'RESET' });
    const photoRow = document.querySelector('[data-personalise-row="photo"]');
    const el = photoRow?.querySelector('[data-photo-error]');
    if (el) el.textContent = 'Re-add your photo to continue.';
  }

  // Foreground/resume controller. Reads the durable snapshot and, if we returned
  // to a stalled or recoverable state, silently re-drives the pipeline. Safe and
  // cheap because the restyle is idempotent on photoUrl (backend memo cache).
  function attemptResume() {
    const snap = readSnapshot(safeSession());
    const decision = decideResume(snap, {
      hasLiveOperation: !!activeOperation,
      hasFile: !!lastFile,
    });
    switch (decision.action) {
      case 'redrive-restyle':
        if (resumeAttempts >= MAX_RESUME_ATTEMPTS) {
          dispatch({ type: 'RESTYLE_FAIL', error: 'Couldn’t reach our styler — tap to retry', retryable: false });
          return;
        }
        resumeAttempts += 1;
        isResuming = true;
        runRestyle(decision.photoUrl);
        break;
      case 'redrive-upload':
        if (resumeAttempts >= MAX_RESUME_ATTEMPTS || !lastFile) return;
        resumeAttempts += 1;
        handlePhotoSelected(lastFile);
        break;
      case 'rehydrate':
        // Restore done-state from the snapshot without any network call,
        // mirroring boot()'s saved-design path.
        dispatch({ type: 'UPLOAD_DONE', photoS3Key: decision.photoS3Key, photoUrl: decision.photoUrl });
        dispatch({ type: 'RESTYLE_DONE', styledUrl: decision.styledUrl, styledKey: decision.styledKey });
        break;
      case 'prompt-readd':
        showReaddPrompt();
        break;
      case 'show-error':
        // Non-retryable failure (e.g. a 4xx bad photo). The live render path
        // already shows the error when state is 'failed'; on a fresh reload
        // (state 'empty') there's nothing to recover, so just drop the spent
        // ticket rather than letting it linger to the TTL.
        if (snap && snap.phase === 'failed' && state.phase !== 'failed') clearSnapshot(safeSession());
        break;
      case 'none':
      default:
        break;
    }
  }

  // ── Boot ──
  async function boot() {
    const host = document.querySelector('[data-personalise-canvas-host]');
    if (!host) return;
    const templateHandle = readTemplateHandle();
    const productHandle = readProductHandle();
    if (!templateHandle) {
      console.warn('[personalise] no template handle on host element');
      return;
    }
    await ensureBundle();
    const { template, design } = await fetchTemplate(templateHandle, productHandle);
    window.PersonaliseCanvas.mount(host, { template });

    if (design?.renderedImageUrl) {
      dispatch({ type: 'UPLOAD_DONE', photoS3Key: design.photoS3Key, photoUrl: design.photoUrl });
      dispatch({ type: 'RESTYLE_DONE', styledUrl: design.styledPhotoUrl, styledKey: design.styledPhotoS3Key });
    } else {
      // No saved design — show the product's placeholder photo inside the
      // SVG photo slot so the design doesn't look broken. State stays in
      // 'empty' phase so ATC remains disabled until a real upload.
      const placeholder = document.querySelector('[data-personalise-placeholder-photo]')?.dataset.personalisePlaceholderPhoto;
      if (placeholder && placeholder.trim()) {
        window.PersonaliseCanvas.setState({ styledPhotoUrl: placeholder });
      }
    }

    // Text row wiring (input → store)
    const textInputs = document.querySelectorAll('[data-personalise-row][data-row-type="text"] input');
    textInputs.forEach(input => {
      const slotId = input.dataset.slotId;
      input.addEventListener('input', () => {
        const tv = { ...window.PersonaliseCanvas.getState().textValues, [slotId]: input.value };
        window.PersonaliseCanvas.setState({ textValues: tv });
        scheduleAutosave();
      });
    });

    // Store → input sync (for inline-editor commits, programmatic updates).
    // Only update inputs not currently focused, so we don't fight the user
    // mid-typing.
    if (typeof window.PersonaliseCanvas.onStateChange === 'function') {
      window.PersonaliseCanvas.onStateChange(state => {
        if (!state?.textValues) return;
        textInputs.forEach(input => {
          if (input === document.activeElement) return;
          const next = state.textValues[input.dataset.slotId];
          if (next != null && input.value !== next) input.value = next;
        });
      });
    }

    // Live-preview → PDP gallery: rasterise the SVG on every state change
    // (debounced) and inject the data URL into every .mock-image__artwork
    // <img> within the PDP scope only — must NOT touch cart drawer items
    // or other product cards on the page.
    let snapshotTimer = null;
    let lastSnapshotUrl = null;
    let isApplyingSnapshot = false; // re-entry guard for MutationObserver
    // Scope: the personalise host's enclosing product-information section.
    // Cached lazily because the gallery may render after boot.
    const getPdpScope = () => host?.closest('.shopify-section') || host?.closest('product-page, product-information, .product-information') || null;
    const applySnapshotToGallery = (url) => {
      if (!url || isApplyingSnapshot) return;
      isApplyingSnapshot = true;
      lastSnapshotUrl = url;
      const scope = getPdpScope();
      if (!scope) { isApplyingSnapshot = false; return; }
      scope.querySelectorAll('.mock-image__artwork').forEach(img => {
        if (img.tagName !== 'IMG') return;
        // Skip if already on this snapshot — every set retriggers the
        // opacity-fade-in transition on css3d slides, which (combined with
        // the MutationObserver re-firing) used to leave them stuck at 0.
        if (img.src === url) return;
        img.src = url;
        img.srcset = '';
        img.removeAttribute('data-srcset');
      });
      // mock-image-lazy skeletons reference data-artwork-src; update so any
      // late hydration shows the snapshot instead of the product image.
      scope.querySelectorAll('.mock-image-lazy').forEach(el => {
        el.setAttribute('data-artwork-src', url);
      });
      // Canvas-mode + css3d-mode mocks: set the container override and
      // dispatch the event the custom element already listens for.
      scope.querySelectorAll('.mock-image-container--canvas, .mock-image-container--css3d').forEach(container => {
        container.dataset.artworkSrc = url;
        container.dispatchEvent(new CustomEvent('artwork-changed', { bubbles: false }));
      });
      // The css3d updateTransform() applies the matrix3d transform that
      // makes the artwork follow the frame's perspective. The element's
      // own artwork-changed handler schedules it via rAF, but Shopify's
      // slideshow keeps off-screen slides at 0-width so the rAF can fire
      // before the slide has a measurable size, leaving the transform
      // blank. Call it directly on every element — it's idempotent and
      // cheap, and it bails internally when the container has no width.
      scope.querySelectorAll('mock-image-css3d').forEach(el => {
        if (typeof el.updateTransform === 'function') el.updateTransform();
      });
      // gallery-wall-frame-mock exposes setArtwork(url) directly.
      scope.querySelectorAll('gallery-wall-frame-mock').forEach(el => {
        if (typeof el.setArtwork === 'function') {
          el.setArtwork(url).catch(() => {});
        }
      });
      // room-background-mock: re-reads data-artwork-urls on every render.
      // Set the dataset and call scheduleRender() — the render cache key
      // includes artworkUrls so the cache invalidates automatically.
      scope.querySelectorAll('room-background-mock').forEach(el => {
        if (el.dataset.artworkUrls === url) return;
        el.dataset.artworkUrls = url;
        // The renderer also reads data-artworks (JSON array of {url,title})
        // — set both so whichever code path runs sees the snapshot.
        try {
          const existing = JSON.parse(el.dataset.artworks || '[]');
          const updated = (existing.length ? existing : [{ url: '', title: '' }]).map(a => ({ ...a, url }));
          el.dataset.artworks = JSON.stringify(updated);
          if (Array.isArray(el.artworks)) el.artworks = updated; // internal state if set
        } catch (_) {
          el.dataset.artworks = JSON.stringify([{ url, title: '' }]);
        }
        if (typeof el.scheduleRender === 'function') el.scheduleRender();
      });
      // pdp-surgical-room-preview reads currentArtworkUrl() which queries
      // style-picker[data-artwork-url] BEFORE [data-artwork-src]. Update
      // the style-picker so the 3D rotate frame + 3D in-room previews see
      // the snapshot, then dispatch the refresh event.
      scope.querySelectorAll('style-picker').forEach(sp => {
        if (sp.dataset.artworkUrl !== url) sp.dataset.artworkUrl = url;
      });
      // Zoom dialog: the zoomed gallery renders the PRODUCT's artwork media
      // (force_no_mock — see _product-media-gallery.liquid) and never hears
      // about the live composite, so full-zoom showed the catalogue image
      // instead of the customer's personalised render. All MOCK| entries
      // dedupe to ONE zoom slide — the first li — so only that slide (and
      // the matching first thumbnail) is replaced; later slides are real
      // auxiliary product media and must keep their own images. Stripping
      // data_max_resolution stops the dialog's scroll-time high-res swap
      // from replacing the snapshot with the original artwork file.
      scope.querySelectorAll('zoom-dialog').forEach(zd => {
        const zoomImg = zd.querySelector('.dialog-zoomed-gallery li img.product-media__image');
        if (zoomImg) {
          if (zoomImg.src !== url) {
            zoomImg.src = url;
            zoomImg.srcset = '';
            zoomImg.removeAttribute('data-srcset');
          }
          zoomImg.removeAttribute('data_max_resolution');
        }
        const thumbImg = zd.querySelector('.dialog-thumbnails-list__thumbnail img');
        if (thumbImg && thumbImg.src !== url) {
          thumbImg.src = url;
          thumbImg.srcset = '';
        }
      });
      document.dispatchEvent(new CustomEvent('style-picker:applied', { detail: { source: 'personalise-canvas' } }));
      isApplyingSnapshot = false;
      // The styled artwork just landed in the gallery — earn the moment.
      if (pendingReveal && state.phase === 'done') {
        pendingReveal = false;
        playReveal();
      }
    };
    const scheduleSnapshot = () => {
      if (snapshotTimer) clearTimeout(snapshotTimer);
      snapshotTimer = setTimeout(async () => {
        try {
          const url = await window.PersonaliseCanvas.snapshot({ width: 1200 });
          applySnapshotToGallery(url);
          // Once a design has settled (photo uploaded + restyled), persist the
          // same composite raster to the cart line so the cart thumbnail shows
          // the full artwork-template preview, not just the styled photo.
          if (state.phase === 'done') schedulePersistRender(url);
        } catch (err) {
          console.warn('[personalise] snapshot failed', err);
        }
      }, 250);
    };
    if (typeof window.PersonaliseCanvas.onStateChange === 'function') {
      window.PersonaliseCanvas.onStateChange(() => scheduleSnapshot());
    }
    // Initial paint: the subscriber above only fires on subsequent state
    // changes, but on a clean page load there are none — so the gallery
    // would stay on the product's featured_image until the user touches
    // something. Kick off one snapshot once the SVG has loaded so the
    // gallery shows the template immediately.
    const waitForSvgThenSnap = (tries = 0) => {
      if (host.querySelector('svg')) {
        scheduleSnapshot();
      } else if (tries < 50) {
        setTimeout(() => waitForSvgThenSnap(tries + 1), 100);
      }
    };
    waitForSvgThenSnap();
    // No MutationObserver — room-background-mock + gallery-wall-frame-mock
    // rebuild their internal DOM on every render, which would loop the
    // observer and constantly restart the css3d opacity transition. The
    // state-change subscriber covers every meaningful re-apply case.

    // Palette swatch wiring
    document.querySelectorAll('[data-personalise-row="palette"] [data-palette-id]').forEach(swatch => {
      swatch.addEventListener('click', () => {
        window.PersonaliseCanvas.setState({ paletteId: swatch.dataset.paletteId });
        document.querySelectorAll('[data-personalise-row="palette"] [data-palette-id]').forEach(s => s.classList.toggle('is-active', s === swatch));
        scheduleAutosave();
      });
    });

    // Photo row wiring
    const photoInput = document.querySelector('[data-personalise-row="photo"] input[type="file"]');
    if (photoInput) {
      photoInput.addEventListener('change', e => {
        const f = e.target.files?.[0];
        if (f) handlePhotoSelected(f);
      });
    }
    const photoButton = document.querySelector('[data-personalise-row="photo"] [data-photo-trigger]');
    photoButton?.addEventListener('click', () => photoInput?.click());

    // Replace + Remove (Done state)
    const photoRow = document.querySelector('[data-personalise-row="photo"]');
    photoRow?.querySelector('[data-photo-replace]')?.addEventListener('click', () => photoInput?.click());
    photoRow?.querySelector('[data-photo-remove]')?.addEventListener('click', () => {
      dispatch({ type: 'RESET' });
      window.PersonaliseCanvas.setState({ styledPhotoUrl: null, styledPhotoS3Key: null, originalPhotoS3Key: null });
      if (photoInput) photoInput.value = '';
    });

    // Retry (Failed state)
    document.addEventListener('click', e => {
      if (e.target.matches('[data-personalise-retry]') && state.phase === 'failed' && state.photoUrl) {
        runRestyle(state.photoUrl);
      }
    });

    // Char count for text inputs — keeps the meta line in sync.
    textInputs.forEach(input => {
      const meta = input.parentElement?.querySelector('[data-text-count]');
      if (!meta) return;
      const max = parseInt(input.getAttribute('maxlength') || '0', 10) || 80;
      const sync = () => {
        const n = input.value.length;
        meta.textContent = `${n} / ${max}`;
        meta.classList.toggle('is-near-cap', max > 0 && n >= max);
      };
      input.addEventListener('input', sync);
      sync();
    });

    // Cart attach
    document.addEventListener('cart:update', async e => {
      const lineKey = e.detail?.resource?.key || e.detail?.resource?.items?.[0]?.key;
      const shortId = document.querySelector('form input[name="properties[_design_id]"]')?.value;
      if (!lineKey || !shortId) return;
      try {
        await fetch(`${readAppUrl()}/api/designs/attach-cart`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ shortId, cartLineKey: lineKey }),
        });
        clearSnapshot(safeSession()); // item is in the cart — the in-progress ticket is spent
      } catch (err) { console.warn('[personalise] attach-cart failed', err); }
    });

    // Mobile resilience: re-drive a stalled/recoverable flow when the page
    // returns to the foreground (app switch) or is restored from bfcache.
    window.addEventListener('pageshow', () => attemptResume());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') attemptResume();
    });
    // Boot-time: if the server didn't already restore a design (state is still
    // 'empty'), try the client snapshot — covers a full page reload mid-flow.
    if (state.phase === 'empty') attemptResume();

    render();
  }

  // Debounced autosave (1.5s after last change)
  let autosaveTimer = null;
  function scheduleAutosave() {
    clearTimeout(autosaveTimer);
    autosaveTimer = setTimeout(async () => {
      try {
        const res = await fetch(`${readAppUrl()}/api/designs/autosave`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            productHandle: readProductHandle(),
            templateId: window.PersonaliseCanvas.getTemplate()?.id,
            configuration: window.PersonaliseCanvas.getState(),
          }),
        });
        // Mirror the design's public id onto the cart form. This is the link
        // the whole post-order pipeline hangs off: the print-order endpoint
        // resolves paletteId/textValues from the Design row via _design_id,
        // and the attach-cart listener (cart:update) reads the same input.
        // Until now nothing ever set it — the input was read but never
        // written, so Design rows were never connected to orders.
        if (res.ok) {
          const { design } = await res.json().catch(() => ({}));
          if (design?.shortId) setCartLineProp('_design_id', design.shortId);
        }
      } catch (err) { console.warn('[personalise] autosave failed', err); }
      if (state.phase === 'done') persistRender();
    }, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
